import {
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Eye,
  Facebook,
  FileText,
  Instagram,
  MapPin,
  Megaphone,
  Minus,
  MousePointerClick,
  Search,
  Share2,
  TrendingUp,
  Users
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis
} from "recharts";
import type {
  MarketingAnalyticsSnapshot,
  MarketingAudienceRow,
  MarketingChannelRow,
  MarketingContentRow,
  MarketingDailyTrendPoint,
  MarketingMetricTrendSet,
  MarketingMetricValue,
  MarketingPageRow,
  MarketingTrendComparison,
  MarketingTrendMetricKey,
  MarketingTrendPeriodKey,
  MarketingTrendSummary,
  MarketingSocialAccountRow,
  MarketingSocialAccountTrendRow,
  MarketingSocialPlatformRow
} from "../../shared/types";
import { ChartContainer, ChartLegendContent, ChartTooltipContent, type ChartConfig } from "../components/charts/ChartPrimitives";
import { StateBlock } from "../components/common/StateBlock";
import { MarketingMetricHelp } from "../components/common/MarketingMetricHelp";
import { StatsCounter } from "../components/common/StatsCounter";
import { getMarketingAnalytics, readCachedMarketingAnalytics } from "../services/marketingService";
import { compareContentAudience, contentAudienceViewRate, contentInPeriod, contentPeriodRange, type ContentPeriod } from "../utils/marketingContent";
import { getMarketingThumbnail } from "../services/marketingThumbnailService";

const numberFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const compactFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1, notation: "compact" });
const percentFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1, style: "percent" });
// Keep the website views available while marketing focuses on social reporting.
const showWebsiteAnalytics = false;

const marketingChartConfig = {
  sessions: { color: "#5ec7ef", label: "Sessions", formatter: formatChartCounter },
  pageViews: { color: "#f0b84b", label: "Page views", formatter: formatChartCounter },
  clicks: { color: "#f36f5f", label: "Clicks", formatter: formatChartCounter },
  activeUsers: { color: "#47d6b1", label: "Active users", formatter: formatChartCounter },
  followers: { color: "#8f7cf7", label: "Followers", formatter: formatChartCounter },
  facebook: { color: "#f5c542", label: "Facebook", formatter: formatChartCounter },
  instagram: { color: "#d99a16", label: "Instagram", formatter: formatChartCounter },
  engagementSeconds: { color: "#b7d877", label: "Engagement seconds", formatter: formatChartCounter }
} satisfies ChartConfig;

const channelPieColors = ["#5ec7ef", "#f0b84b", "#47d6b1", "#f36f5f", "#f5c542", "#8f7cf7", "#aeb9b4"];

function formatChartNumber(value: unknown): string {
  const numericValue = Array.isArray(value) ? Number(value[0] ?? 0) : Number(value ?? 0);
  return compactFormatter.format(Number.isFinite(numericValue) ? numericValue : 0);
}

function formatChartCounter(value: unknown): ReactNode {
  const numericValue = Array.isArray(value) ? Number(value[0] ?? 0) : Number(value ?? 0);
  const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
  return <StatsCounter formatValue={formatChartNumber} value={safeValue} />;
}

function formatNumber(value: number): string {
  return Math.abs(value) >= 10000 ? compactFormatter.format(value) : numberFormatter.format(value);
}

function formatNullablePercent(value: number | null | undefined): string {
  return value === null || value === undefined ? "n/a" : percentFormatter.format(value);
}

function formatMetricValue(metric: MarketingMetricValue): string {
  if (metric.unit === "percent") {
    return percentFormatter.format(metric.value);
  }
  if (metric.unit === "seconds") {
    return `${metric.value.toLocaleString(undefined, { maximumFractionDigits: 1 })}s`;
  }
  return formatNumber(metric.value);
}

function MetricCounter({ duration, value, formatValue = formatNumber }: { duration?: number; value: number; formatValue?: (value: number) => string }) {
  return <StatsCounter duration={duration} formatValue={formatValue} value={value} />;
}

function NullableMetricCounter({ value, formatValue = formatNumber }: { value: number | null | undefined; formatValue?: (value: number) => string }) {
  return value === null || value === undefined ? <>n/a</> : <MetricCounter formatValue={formatValue} value={value} />;
}

const trendPeriodLabels: Array<{ fullLabel: string; key: MarketingTrendPeriodKey; label: string }> = [
  { key: "day", label: "T-1", fullLabel: "T-2 vs T-1" },
  { key: "wtd", label: "WTD", fullLabel: "Week to date" },
  { key: "mtd", label: "MTD", fullLabel: "Month to date" },
  { key: "mom", label: "MoM", fullLabel: "Month over month" }
];

const stationTrendPeriodLabels: Array<{ fullLabel: string; key: StationTrendPeriodKey; label: string }> = [
  { key: "day", label: "T-1", fullLabel: "Previous day" },
  { key: "week", label: "WoW", fullLabel: "Previous week" },
  { key: "month", label: "MoM", fullLabel: "Previous month" }
];

const webTrendMetricKeys: MarketingTrendMetricKey[] = [
  "activeUsers",
  "eventCount",
  "keyEvents",
  "newUsers",
  "sessions",
  "pageViews",
  "clicks",
  "engagementSecondsPerSession"
];

type MarketingPageView = "overview" | "station-sns";
type MarketingPlatformKey = "facebook" | "instagram";
type StationSocialMetricKey = "engagements" | "followers" | "reach" | "views";
type StationTrendPeriodKey = "day" | "month" | "week";
type StationMetricTrendSet = Partial<Record<StationTrendPeriodKey, MarketingTrendComparison>>;
type StationMetricTrendSummary = Partial<Record<StationSocialMetricKey, StationMetricTrendSet>>;

interface StationSocialMetrics {
  contentCount: number;
  engagementRate: number | null;
  engagements: number;
  followers: number;
  reach: number;
  views: number;
}

interface StationPlatformSummary extends StationSocialMetrics {
  hasData: boolean;
  platform: MarketingPlatformKey;
  trends: StationMetricTrendSummary;
}

interface StationSocialSummary {
  accountRows: MarketingSocialAccountRow[];
  contentRows: MarketingContentRow[];
  key: string;
  name: string;
  platforms: StationPlatformSummary[];
  totals: StationSocialMetrics;
}

interface StationSocialAccumulator {
  accountRows: MarketingSocialAccountRow[];
  accountMetrics: Record<MarketingPlatformKey, StationSocialMetrics>;
  contentMetrics: Record<MarketingPlatformKey, StationSocialMetrics>;
  contentRows: MarketingContentRow[];
  names: Map<string, number>;
}

interface StationTrendAccumulator {
  dateMetrics: Map<string, StationSocialMetrics>;
}

function parseMarketingDate(value: string): Date {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

function formatMarketingDate(value: Date): string {
  return value.toISOString().slice(0, 10);
}

function addMarketingDays(value: Date, days: number): Date {
  const nextValue = new Date(value);
  nextValue.setUTCDate(nextValue.getUTCDate() + days);
  return nextValue;
}

function addMarketingMonths(value: Date, months: number): Date {
  const targetYear = value.getUTCFullYear();
  const targetMonth = value.getUTCMonth() + months;
  const targetMonthStart = new Date(Date.UTC(targetYear, targetMonth, 1));
  const targetDay = Math.min(value.getUTCDate(), marketingDaysInMonth(targetMonthStart));
  return new Date(Date.UTC(targetMonthStart.getUTCFullYear(), targetMonthStart.getUTCMonth(), targetDay));
}

function marketingMonthStart(value: Date): Date {
  return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), 1));
}

function marketingDaysInMonth(value: Date): number {
  return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth() + 1, 0)).getUTCDate();
}

function marketingWeekStartMonday(value: Date): Date {
  const mondayOffset = (value.getUTCDay() + 6) % 7;
  return addMarketingDays(value, -mondayOffset);
}

function buildTrendComparison(current: number, previous: number): MarketingTrendComparison {
  if (!previous) {
    return current ? { current, previous } : { current, previous, change: 0 };
  }
  return {
    current,
    previous,
    change: (current - previous) / Math.abs(previous)
  };
}

function sumDailyTrendMetric(rows: MarketingDailyTrendPoint[], start: Date, end: Date, key: MarketingTrendMetricKey): number {
  const startKey = formatMarketingDate(start);
  const endKey = formatMarketingDate(end);
  const matchingRows = rows.filter((row) => row.date >= startKey && row.date <= endKey);
  const totals = matchingRows.reduce((nextTotals, row) => ({
    activeUsers: nextTotals.activeUsers + row.activeUsers,
    clicks: nextTotals.clicks + row.clicks,
    engagementSeconds: nextTotals.engagementSeconds + row.engagementSeconds,
    eventCount: nextTotals.eventCount + row.eventCount,
    keyEvents: nextTotals.keyEvents + row.keyEvents,
    newUsers: nextTotals.newUsers + row.newUsers,
    pageViews: nextTotals.pageViews + row.pageViews,
    sessions: nextTotals.sessions + row.sessions
  }), {
    activeUsers: 0,
    clicks: 0,
    engagementSeconds: 0,
    eventCount: 0,
    keyEvents: 0,
    newUsers: 0,
    pageViews: 0,
    sessions: 0
  });

  if (key === "engagementSecondsPerSession") {
    return totals.sessions ? totals.engagementSeconds / totals.sessions : 0;
  }

  if (key === "activeUsers" || key === "eventCount" || key === "newUsers" || key === "sessions" || key === "pageViews" || key === "clicks" || key === "keyEvents") {
    return totals[key];
  }

  return 0;
}

function buildDailyTrendSummary(rows: MarketingDailyTrendPoint[], asOfDate?: string): MarketingTrendSummary {
  if (!asOfDate || rows.length === 0) {
    return {};
  }

  const asOf = parseMarketingDate(asOfDate);
  const currentWeekStart = marketingWeekStartMonday(asOf);
  const currentMonthStart = marketingMonthStart(asOf);
  const previousMonthStart = new Date(Date.UTC(asOf.getUTCFullYear(), asOf.getUTCMonth() - 1, 1));
  const monthDayOffset = asOf.getUTCDate() - 1;
  const previousMonthEnd = addMarketingDays(previousMonthStart, Math.min(monthDayOffset, marketingDaysInMonth(previousMonthStart) - 1));
  const ranges: Record<MarketingTrendPeriodKey, { currentEnd: Date; currentStart: Date; previousEnd: Date; previousStart: Date }> = {
    day: {
      currentStart: asOf,
      currentEnd: asOf,
      previousStart: addMarketingDays(asOf, -1),
      previousEnd: addMarketingDays(asOf, -1)
    },
    wtd: {
      currentStart: currentWeekStart,
      currentEnd: asOf,
      previousStart: addMarketingDays(currentWeekStart, -7),
      previousEnd: addMarketingDays(asOf, -7)
    },
    mtd: {
      currentStart: currentMonthStart,
      currentEnd: asOf,
      previousStart: previousMonthStart,
      previousEnd: previousMonthEnd
    },
    mom: {
      currentStart: addMarketingDays(asOf, -29),
      currentEnd: asOf,
      previousStart: addMarketingDays(asOf, -59),
      previousEnd: addMarketingDays(asOf, -30)
    }
  };

  return webTrendMetricKeys.reduce<MarketingTrendSummary>((summary, metricKey) => {
    summary[metricKey] = (Object.keys(ranges) as MarketingTrendPeriodKey[]).reduce<MarketingMetricTrendSet>((trendSet, periodKey) => {
      const range = ranges[periodKey];
      trendSet[periodKey] = buildTrendComparison(
        sumDailyTrendMetric(rows, range.currentStart, range.currentEnd, metricKey),
        sumDailyTrendMetric(rows, range.previousStart, range.previousEnd, metricKey)
      );
      return trendSet;
    }, {});
    return summary;
  }, {});
}

function mergeTrendSummary(snapshot: MarketingAnalyticsSnapshot): MarketingTrendSummary {
  const fallback = buildDailyTrendSummary(snapshot.dailyTrend, snapshot.asOfDate);
  const source = snapshot.trendSummary || {};
  const keys = new Set([...Object.keys(fallback), ...Object.keys(source)] as MarketingTrendMetricKey[]);

  return [...keys].reduce<MarketingTrendSummary>((summary, metricKey) => {
    summary[metricKey] = {
      ...fallback[metricKey],
      ...source[metricKey]
    };
    return summary;
  }, {});
}

function trendTone(comparison?: MarketingTrendComparison): "down" | "flat" | "new" | "up" {
  if (!comparison) {
    return "flat";
  }
  const delta = comparison.current - comparison.previous;
  if (delta > 0) {
    return "up";
  }
  if (delta < 0) {
    return "down";
  }
  return "flat";
}

function formatTrendChange(comparison?: MarketingTrendComparison): string {
  if (!comparison) {
    return "n/a";
  }
  if (comparison.change === undefined) {
    return comparison.current ? "New" : "0.0%";
  }
  const sign = comparison.change > 0 ? "+" : "";
  const percentage = comparison.change * 100;
  const absolutePercentage = Math.abs(percentage);
  const formattedPercentage = absolutePercentage >= 1000
    ? `${compactFormatter.format(absolutePercentage)}%`
    : `${absolutePercentage.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
  return `${sign}${comparison.change < 0 ? "-" : ""}${formattedPercentage}`;
}

function formatTrendDelta(comparison?: MarketingTrendComparison): string {
  if (!comparison) {
    return "n/a";
  }
  if (comparison.change === undefined) {
    return comparison.current ? "n/a" : "0";
  }
  const delta = comparison.current - comparison.previous;
  if (!delta) {
    return "0";
  }
  return `${delta > 0 ? "+" : ""}${formatNumber(delta)}`;
}

function formatStationTrendCount(comparison?: MarketingTrendComparison): string {
  if (!comparison) {
    return "n/a";
  }
  const delta = comparison.current - comparison.previous;
  if (!delta) {
    return "0";
  }
  return `${delta > 0 ? "+" : ""}${formatNumber(delta)}`;
}

function formatDate(value?: string): string {
  if (!value) {
    return "n/a";
  }
  const businessDate = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const date = businessDate
    ? new Date(Date.UTC(Number(businessDate[1]), Number(businessDate[2]) - 1, Number(businessDate[3]), 12))
    : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", timeZone: "America/Toronto" }).format(date);
}

function formatDateTime(value?: string): string {
  if (!value) {
    return "n/a";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    month: "short",
    timeZone: "America/Toronto"
  }).format(date);
}

function formatAxisLabel(value: string): string {
  return value.length > 18 ? `${value.slice(0, 17)}...` : value;
}

function platformLabel(value: string): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : "Source";
}

function normalizePlatform(value?: string): "facebook" | "instagram" {
  return value?.toLowerCase() === "instagram" ? "instagram" : "facebook";
}

function normalizeStationSourceName(value: string): string {
  return value.trim() || "Unknown station";
}

function stationGroupKey(value: string): string {
  return normalizeStationSourceName(value)
    .toLowerCase()
    .replace(/gen\s*7/g, "gen7")
    .replace(/charlie['’]?s/g, "charlies")
    .replace(/[^a-z0-9]+/g, " ")
    .trim() || "unknown";
}

function emptyStationSocialMetrics(): StationSocialMetrics {
  return {
    contentCount: 0,
    engagementRate: null,
    engagements: 0,
    followers: 0,
    reach: 0,
    views: 0
  };
}

function emptyStationMetricSet(): Record<MarketingPlatformKey, StationSocialMetrics> {
  return {
    facebook: emptyStationSocialMetrics(),
    instagram: emptyStationSocialMetrics()
  };
}

function ensureStationGroup(groups: Map<string, StationSocialAccumulator>, sourceName: string): StationSocialAccumulator {
  const displayName = normalizeStationSourceName(sourceName);
  const key = stationGroupKey(displayName);
  if (!groups.has(key)) {
    groups.set(key, {
      accountRows: [],
      accountMetrics: emptyStationMetricSet(),
      contentMetrics: emptyStationMetricSet(),
      contentRows: [],
      names: new Map()
    });
  }

  const group = groups.get(key)!;
  group.names.set(displayName, (group.names.get(displayName) || 0) + 1);
  return group;
}

function pickStationName(names: Map<string, number>): string {
  return [...names.entries()]
    .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))[0]?.[0] || "Unknown station";
}

function contentEngagementTotal(row: MarketingContentRow): number {
  return row.engagementTotal || row.likes + row.comments + row.shares + row.saves;
}

function contentPublishedTime(row: MarketingContentRow): number {
  const value = row.publishedAt ? Date.parse(row.publishedAt) : Number.NaN;
  return Number.isFinite(value) ? value : 0;
}

function stationTrendValueOnOrBefore(
  dateMetrics: Map<string, StationSocialMetrics>,
  metricKey: StationSocialMetricKey,
  date: string
): number {
  const matchingDate = [...dateMetrics.keys()]
    .filter((metricDate) => metricDate <= date)
    .sort()
    .pop();

  if (!matchingDate) {
    return 0;
  }

  return dateMetrics.get(matchingDate)?.[metricKey] || 0;
}

function stationTrendComparison(dateMetrics: Map<string, StationSocialMetrics>, metricKey: StationSocialMetricKey, latestDate: string, daysBack?: number, monthsBack?: number): MarketingTrendComparison {
  const latest = parseMarketingDate(latestDate);
  const compareDate = monthsBack
    ? addMarketingMonths(latest, -monthsBack)
    : addMarketingDays(latest, -(daysBack || 1));
  return buildTrendComparison(
    stationTrendValueOnOrBefore(dateMetrics, metricKey, latestDate),
    stationTrendValueOnOrBefore(dateMetrics, metricKey, formatMarketingDate(compareDate))
  );
}

function buildStationMetricTrendSummary(dateMetrics: Map<string, StationSocialMetrics>): StationMetricTrendSummary {
  const latestDate = [...dateMetrics.keys()].sort().pop();
  if (!latestDate) {
    return {};
  }

  return (["followers", "views", "engagements", "reach"] as StationSocialMetricKey[]).reduce<StationMetricTrendSummary>((summary, metricKey) => {
    summary[metricKey] = {
      day: stationTrendComparison(dateMetrics, metricKey, latestDate, 1),
      week: stationTrendComparison(dateMetrics, metricKey, latestDate, 7),
      month: stationTrendComparison(dateMetrics, metricKey, latestDate, undefined, 1)
    };
    return summary;
  }, {});
}

function buildStationSocialTrendSummaries(rows: MarketingSocialAccountTrendRow[]): Map<string, Record<MarketingPlatformKey, StationMetricTrendSummary>> {
  const trendGroups = new Map<string, Record<MarketingPlatformKey, StationTrendAccumulator>>();
  const ensurePlatformGroup = (stationKey: string, platform: MarketingPlatformKey) => {
    if (!trendGroups.has(stationKey)) {
      trendGroups.set(stationKey, {
        facebook: { dateMetrics: new Map() },
        instagram: { dateMetrics: new Map() }
      });
    }
    return trendGroups.get(stationKey)![platform];
  };

  rows.forEach((row) => {
    if (!row.date) {
      return;
    }

    const platform = normalizePlatform(row.platform);
    const group = ensurePlatformGroup(stationGroupKey(row.sourceName), platform);
    const metrics = group.dateMetrics.get(row.date) || emptyStationSocialMetrics();
    metrics.followers += row.followers;
    metrics.reach += row.reach;
    metrics.views += row.views;
    metrics.engagements += row.engagements;
    group.dateMetrics.set(row.date, metrics);
  });

  return [...trendGroups.entries()].reduce<Map<string, Record<MarketingPlatformKey, StationMetricTrendSummary>>>((summaries, [stationKey, platformGroups]) => {
    summaries.set(stationKey, {
      facebook: buildStationMetricTrendSummary(platformGroups.facebook.dateMetrics),
      instagram: buildStationMetricTrendSummary(platformGroups.instagram.dateMetrics)
    });
    return summaries;
  }, new Map());
}

function withEngagementRate(metrics: StationSocialMetrics): StationSocialMetrics {
  return {
    ...metrics,
    engagementRate: metrics.reach > 0 ? metrics.engagements / metrics.reach : null
  };
}

function mergeStationPlatformMetrics(accountMetrics: StationSocialMetrics, contentMetrics: StationSocialMetrics, platform: MarketingPlatformKey): StationSocialMetrics {
  return withEngagementRate({
    contentCount: contentMetrics.contentCount,
    engagementRate: null,
    engagements: accountMetrics.engagements || contentMetrics.engagements,
    followers: accountMetrics.followers,
    reach: platform === "facebook" ? accountMetrics.reach : accountMetrics.reach || contentMetrics.reach,
    views: accountMetrics.views || contentMetrics.views
  });
}

function buildStationSocialSummaries(snapshot: MarketingAnalyticsSnapshot): StationSocialSummary[] {
  const groups = new Map<string, StationSocialAccumulator>();
  const trendSummaries = buildStationSocialTrendSummaries(snapshot.socialAccountTrend || []);
  const platformOrder: MarketingPlatformKey[] = ["facebook", "instagram"];

  snapshot.socialAccounts.forEach((account) => {
    const group = ensureStationGroup(groups, account.sourceName);
    const platform = normalizePlatform(account.platform);
    const metrics = group.accountMetrics[platform];
    group.accountRows.push(account);
    metrics.followers += account.followers;
    metrics.reach += account.reach;
    metrics.views += account.views + account.videoViews;
    metrics.engagements += account.postEngagements;
  });

  snapshot.content.forEach((content) => {
    const group = ensureStationGroup(groups, content.sourceName);
    const platform = normalizePlatform(content.platform);
    const metrics = group.contentMetrics[platform];
    group.contentRows.push(content);
    metrics.contentCount += 1;
    metrics.reach += content.reach || 0;
    metrics.views += content.views || 0;
    metrics.engagements += contentEngagementTotal(content);
  });

  return [...groups.entries()]
    .map(([key, group]) => {
      const platforms = platformOrder.map<StationPlatformSummary>((platform) => {
        const metrics = mergeStationPlatformMetrics(group.accountMetrics[platform], group.contentMetrics[platform], platform);
        return {
          ...metrics,
          hasData: metrics.followers > 0 || metrics.reach > 0 || metrics.views > 0 || metrics.engagements > 0 || metrics.contentCount > 0,
          platform,
          trends: trendSummaries.get(key)?.[platform] || {}
        };
      });
      const totals = withEngagementRate(platforms.reduce<StationSocialMetrics>((nextTotals, platform) => ({
        contentCount: nextTotals.contentCount + platform.contentCount,
        engagementRate: null,
        engagements: nextTotals.engagements + platform.engagements,
        followers: nextTotals.followers + platform.followers,
        reach: nextTotals.reach + platform.reach,
        views: nextTotals.views + platform.views
      }), emptyStationSocialMetrics()));

      return {
        accountRows: [...group.accountRows].sort((left, right) => normalizePlatform(left.platform).localeCompare(normalizePlatform(right.platform))),
        contentRows: group.contentRows,
        key,
        name: pickStationName(group.names),
        platforms,
        totals
      };
    })
    .sort((left, right) => (
      right.totals.engagements - left.totals.engagements ||
      right.totals.views - left.totals.views ||
      right.totals.followers - left.totals.followers ||
      left.name.localeCompare(right.name)
    ));
}

function stationMetricSplit(station: StationSocialSummary, metric: keyof Pick<StationSocialMetrics, "engagements" | "followers" | "reach" | "views">): string {
  const facebook = station.platforms.find((platform) => platform.platform === "facebook");
  const instagram = station.platforms.find((platform) => platform.platform === "instagram");
  return `Facebook ${formatNumber(facebook?.[metric] || 0)} / Instagram ${formatNumber(instagram?.[metric] || 0)}`;
}

function stationMetric(label: string, value: number, helper: string): MarketingMetricValue {
  return { helper, label, unit: "number", value };
}

function resolveSocialPlatforms(snapshot: MarketingAnalyticsSnapshot): MarketingSocialPlatformRow[] {
  if (snapshot.socialPlatforms?.length) {
    return snapshot.socialPlatforms;
  }

  const rows = new Map<string, MarketingSocialPlatformRow>();
  const ensureRow = (platform: string) => {
    const normalized = normalizePlatform(platform);
    if (!rows.has(normalized)) {
      rows.set(normalized, {
        platform: normalized,
        followers: 0,
        reach: 0,
        profileViews: 0,
        views: 0,
        videoViews: 0,
        accountEngagements: 0,
        contentEngagements: 0,
        engagements: 0
      });
    }
    return rows.get(normalized)!;
  };

  snapshot.socialAccounts.forEach((account) => {
    const row = ensureRow(account.platform);
    row.followers += account.followers;
    row.reach += account.reach;
    row.profileViews += account.profileViews;
    row.views += account.views;
    row.videoViews += account.videoViews;
    row.accountEngagements += account.postEngagements;
  });

  snapshot.content.forEach((content) => {
    const row = ensureRow(content.platform);
    row.contentEngagements += content.engagementTotal || content.likes + content.comments + content.shares + content.saves;
  });

  return [...rows.values()].map((row) => ({
    ...row,
    engagements: row.accountEngagements + row.contentEngagements
  }));
}

function socialPlatformValue(row: MarketingSocialPlatformRow | undefined, metric: "engagements" | "followers" | "views"): number {
  if (!row) {
    return 0;
  }
  if (metric === "views") {
    return row.views + row.videoViews;
  }
  return row[metric];
}

function socialPlatformSplit(rows: MarketingSocialPlatformRow[], metric: "engagements" | "followers" | "views"): string {
  const facebook = rows.find((row) => normalizePlatform(row.platform) === "facebook");
  const instagram = rows.find((row) => normalizePlatform(row.platform) === "instagram");
  return `Facebook ${formatNumber(socialPlatformValue(facebook, metric))} / Instagram ${formatNumber(socialPlatformValue(instagram, metric))}`;
}

function withHelper(metric: MarketingMetricValue, helper: string): MarketingMetricValue {
  return { ...metric, helper };
}

function SocialPlatformBadge({ platform }: { platform: "facebook" | "instagram" }) {
  const Icon = platform === "facebook" ? Facebook : Instagram;
  return (
    <span className={`marketing-social-platform-label marketing-social-platform-label--${platform}`}>
      <span className="marketing-social-platform-icon"><Icon size={16} aria-hidden="true" /></span>
      <span>{platformLabel(platform)}</span>
    </span>
  );
}

function firstKnownGeo(snapshot: MarketingAnalyticsSnapshot): string {
  const known = snapshot.geography.find((row) => row.city !== "(unknown)" || row.country !== "(unknown)");
  if (!known) {
    return "GA4 web geography is not populated yet";
  }
  return [known.city, known.region, known.country].filter((part) => part && part !== "(unknown)").join(", ");
}

function isKnownDemographicValue(value?: string): value is string {
  return Boolean(value && value !== "(unknown)" && value.toLowerCase() !== "unknown");
}

function normalizeGenderLabel(value: string): string {
  const normalized = value.trim().toLowerCase();
  if (normalized === "f" || normalized === "female") {
    return "Female";
  }
  if (normalized === "m" || normalized === "male") {
    return "Male";
  }
  if (normalized === "u" || normalized === "unknown" || normalized === "unspecified") {
    return "Unspecified";
  }
  return value;
}

function ageSortValue(label: string): number {
  if (label === "65+") {
    return 65;
  }
  const match = label.match(/^(\d+)/);
  return match ? Number(match[1]) : 999;
}

function buildAudienceBreakdown(
  rows: MarketingAudienceRow[],
  dimension: "age" | "gender" | "region"
): Array<{ facebook: number; followers: number; instagram: number; label: string }> {
  const totals = new Map<string, { facebook: number; instagram: number }>();

  rows.forEach((row) => {
    const followers = row.followers || 0;
    if (followers <= 0) {
      return;
    }

    const label = dimension === "age"
      ? isKnownDemographicValue(row.ageBracket) ? row.ageBracket : undefined
      : dimension === "gender"
        ? isKnownDemographicValue(row.gender) ? normalizeGenderLabel(row.gender) : undefined
        : isKnownDemographicValue(row.city)
          ? row.city
          : isKnownDemographicValue(row.country)
            ? row.country
            : undefined;

    if (!label) {
      return;
    }

    const current = totals.get(label) || { facebook: 0, instagram: 0 };
    current[normalizePlatform(row.platform)] += followers;
    totals.set(label, current);
  });

  return [...totals.entries()]
    .map(([label, values]) => ({ label, ...values, followers: values.facebook + values.instagram }))
    .sort((left, right) => {
      if (dimension === "age") {
        return ageSortValue(left.label) - ageSortValue(right.label);
      }
      return right.followers - left.followers;
    });
}

function TrendValue({ comparison, value, valueMode = "percent" }: { comparison?: MarketingTrendComparison; value: string; valueMode?: "delta" | "percent" }) {
  const tone = value === "n/a" ? "flat" : trendTone(comparison);
  const Icon = tone === "up" ? ArrowUpRight : tone === "down" ? ArrowDownRight : Minus;
  const numericValue = !comparison
    ? undefined
    : valueMode === "delta"
      ? (value === "n/a" ? undefined : comparison.current - comparison.previous)
      : comparison.change === undefined
        ? (value === "0.0%" ? 0 : undefined)
        : comparison.change * 100;
  const formatTrendCounter = (nextValue: number) => {
    if (valueMode === "delta") {
      return `${nextValue > 0 ? "+" : ""}${formatNumber(nextValue)}`;
    }
    const absoluteValue = Math.abs(nextValue);
    const formatted = absoluteValue >= 1000
      ? compactFormatter.format(absoluteValue)
      : absoluteValue.toLocaleString(undefined, { maximumFractionDigits: 1 });
    return `${nextValue > 0 ? "+" : nextValue < 0 ? "-" : ""}${formatted}%`;
  };
  return (
    <strong className={`marketing-change marketing-change--${tone}`}>
      <Icon size={13} aria-hidden="true" />
      {numericValue === undefined ? value : <MetricCounter duration={0.8} formatValue={formatTrendCounter} value={numericValue} />}
    </strong>
  );
}

function MarketingTrendStack({ trend, valueMode = "percent", variant = "compact" }: { trend?: MarketingMetricTrendSet; valueMode?: "delta" | "percent"; variant?: "compact" | "panel" }) {
  return (
    <div className={`marketing-trend-stack marketing-trend-stack--${variant}`} aria-label="Period change">
      {trendPeriodLabels.map((period) => {
        const comparison = trend?.[period.key];
        return (
          <span className="marketing-trend-chip" key={period.key} title={`${period.fullLabel}: ${valueMode === "delta" ? "count change" : "percentage change"}`}>
            <span>{variant === "panel" ? period.fullLabel : period.label}</span>
            <TrendValue comparison={comparison} value={valueMode === "delta" ? formatTrendDelta(comparison) : formatTrendChange(comparison)} valueMode={valueMode} />
          </span>
        );
      })}
    </div>
  );
}

function StationTrendStack({ trend }: { trend?: StationMetricTrendSet }) {
  return (
    <div className="marketing-station-trend-stack" aria-label="Station period count change">
      {stationTrendPeriodLabels.map((period) => {
        const comparison = trend?.[period.key];
        return (
          <span className="marketing-station-trend-chip" key={period.key} title={`${period.fullLabel}: count change`}>
            <span>{period.label}</span>
            <TrendValue comparison={comparison} value={formatStationTrendCount(comparison)} valueMode="delta" />
          </span>
        );
      })}
    </div>
  );
}

function MarketingTrendHover({ trend }: { trend?: MarketingMetricTrendSet }) {
  return (
    <div className="marketing-trend-hover">
      <span className="marketing-trend-hover__trigger" tabIndex={0} aria-label="Trend comparisons">
        <TrendingUp size={14} aria-hidden="true" />
      </span>
      <div className="marketing-trend-hover__panel">
        <MarketingTrendStack trend={trend} variant="panel" />
      </div>
    </div>
  );
}

function MarketingKpiTile({ icon, metric, tone, trend, trendValueMode = "percent" }: { icon: ReactNode; metric: MarketingMetricValue; tone: "traffic" | "clicks" | "social" | "engagement"; trend?: MarketingMetricTrendSet; trendValueMode?: "delta" | "percent" }) {
  return (
    <div className={`marketing-kpi marketing-kpi--${tone}`}>
      <div className="marketing-kpi__label">
        {icon}
        <span>{tone === "social" || tone === "engagement" ? <MarketingMetricHelp label={metric.label} /> : metric.label}</span>
      </div>
      <div className={`marketing-kpi__body${trend ? "" : " marketing-kpi__body--single"}`}>
        <strong><MetricCounter formatValue={(value) => formatMetricValue({ ...metric, value })} value={metric.value} /></strong>
        {trend && <MarketingTrendStack trend={trend} valueMode={trendValueMode} />}
      </div>
      {metric.helper && <small>{metric.helper}</small>}
    </div>
  );
}

function MarketingInsightCard({ card }: { card: { helper: string; icon: ReactNode; label: string; trend?: MarketingMetricTrendSet; value: string } }) {
  return (
    <section className="marketing-insight">
      <div className="marketing-insight__top">
        {card.icon}
        <MarketingTrendHover trend={card.trend} />
      </div>
      <span>{card.label}</span>
      <strong>{card.value}</strong>
      <small>{card.helper}</small>
    </section>
  );
}

function MarketingPlatformMetric({ helper, label, tone, trend, trendValueMode = "percent", value }: { helper: string; label: string; tone: "facebook" | "instagram"; trend?: MarketingMetricTrendSet; trendValueMode?: "delta" | "percent"; value: number }) {
  return (
    <div className={`marketing-platform-metric marketing-platform-metric--${tone}`}>
      <span><MarketingMetricHelp label={label} /></span>
      <div className="marketing-platform-metric__body">
        <strong><MetricCounter value={value} /></strong>
        {trend && <MarketingTrendStack trend={trend} valueMode={trendValueMode} />}
      </div>
      <small>{helper}</small>
    </div>
  );
}

function StationPlatformMetric({ helper, label, metricKey, platform, value }: { helper: string; label: string; metricKey: StationSocialMetricKey; platform: StationPlatformSummary; value: number }) {
  return (
    <div className={`marketing-platform-metric marketing-platform-metric--${platform.platform} marketing-platform-metric--station`}>
      <span><MarketingMetricHelp label={label} /></span>
      <div className="marketing-platform-metric__body marketing-platform-metric__body--station">
        <strong><MetricCounter value={value} /></strong>
        <StationTrendStack trend={platform.trends[metricKey]} />
      </div>
      <small>{helper}</small>
    </div>
  );
}

function ChartPanel({ children, eyebrow, icon, title, trend }: { children: ReactNode; eyebrow: string; icon: ReactNode; title: string; trend?: MarketingMetricTrendSet }) {
  return (
    <section className="panel marketing-chart-panel">
      <div className="panel-heading">
        <div>
          <p>{eyebrow}</p>
          <h3>{title}</h3>
        </div>
        <div className="marketing-panel-actions">
          {trend && <MarketingTrendHover trend={trend} />}
          {icon}
        </div>
      </div>
      {children}
    </section>
  );
}

function DailyTrendChart({ snapshot }: { snapshot: MarketingAnalyticsSnapshot }) {
  const data = snapshot.dailyTrend.map((point) => ({
    ...point,
    label: formatDate(point.date)
  }));

  return (
    <ChartContainer config={marketingChartConfig} height={310} minHeight={260}>
      <ComposedChart data={data} margin={{ bottom: 8, left: 0, right: 4, top: 8 }}>
        <CartesianGrid stroke="var(--color-chart-grid)" vertical={false} />
        <XAxis dataKey="label" stroke="var(--color-text-subtle)" tickLine={false} />
        <YAxis stroke="var(--color-text-subtle)" tickFormatter={formatChartNumber} tickLine={false} width={48} />
        <RechartsTooltip content={<ChartTooltipContent config={marketingChartConfig} />} cursor={{ fill: "var(--color-chart-cursor)" }} />
        <Legend content={<ChartLegendContent config={marketingChartConfig} />} />
        <Bar dataKey="clicks" fill="var(--color-clicks)" name="Clicks" radius={[5, 5, 0, 0]} />
        <Line dataKey="sessions" dot={false} name="Sessions" stroke="var(--color-sessions)" strokeWidth={2.4} type="monotone" />
        <Line dataKey="pageViews" dot={false} name="Page views" stroke="var(--color-pageViews)" strokeWidth={2.4} type="monotone" />
      </ComposedChart>
    </ChartContainer>
  );
}

function HourlyTrafficChart({ snapshot }: { snapshot: MarketingAnalyticsSnapshot }) {
  return (
    <ChartContainer config={marketingChartConfig} height={310} minHeight={260}>
      <ComposedChart data={snapshot.hourlyTraffic} margin={{ bottom: 8, left: 0, right: 4, top: 8 }}>
        <CartesianGrid stroke="var(--color-chart-grid)" vertical={false} />
        <XAxis dataKey="hourLabel" interval={1} stroke="var(--color-text-subtle)" tickLine={false} />
        <YAxis stroke="var(--color-text-subtle)" tickFormatter={formatChartNumber} tickLine={false} width={42} />
        <RechartsTooltip content={<ChartTooltipContent config={marketingChartConfig} secondaryKey="daypart" />} cursor={{ fill: "var(--color-chart-cursor)" }} />
        <Legend content={<ChartLegendContent config={marketingChartConfig} />} />
        <Bar dataKey="clicks" fill="var(--color-clicks)" name="Clicks" radius={[5, 5, 0, 0]} />
        <Line dataKey="sessions" dot={false} name="Sessions" stroke="var(--color-sessions)" strokeWidth={2.4} type="monotone" />
      </ComposedChart>
    </ChartContainer>
  );
}

function ChannelChart({ rows }: { rows: MarketingChannelRow[] }) {
  const sortedRows = [...rows].sort((left, right) => right.sessions - left.sessions);
  const data = sortedRows.slice(0, 8).map((row) => ({
    ...row,
    label: `${row.channelGroup}: ${row.sourceMedium}`
  }));
  const pieTopRows = sortedRows.slice(0, 6);
  const otherSessions = sortedRows.slice(6).reduce((total, row) => total + row.sessions, 0);
  const pieData: Array<{ label: string; sessions: number; sourceMedium: string }> = [
    ...pieTopRows.map((row) => ({
      label: row.channelGroup,
      sessions: row.sessions,
      sourceMedium: `${row.channelGroup} / ${row.sourceMedium}`
    })),
    ...(otherSessions > 0
      ? [{ label: "Other", sessions: otherSessions, sourceMedium: "Other channels" }]
      : [])
  ];

  return (
    <div className="marketing-channel-split">
      <ChartContainer config={marketingChartConfig} height={Math.max(260, data.length * 42)} minHeight={250}>
        <BarChart data={data} layout="vertical" margin={{ bottom: 8, left: 12, right: 20, top: 8 }}>
          <CartesianGrid horizontal={false} stroke="var(--color-chart-grid)" />
          <XAxis stroke="var(--color-text-subtle)" tickFormatter={formatChartNumber} tickLine={false} type="number" />
          <YAxis dataKey="label" stroke="var(--color-text-subtle)" tickFormatter={formatAxisLabel} tickLine={false} type="category" width={142} />
          <RechartsTooltip content={<ChartTooltipContent config={marketingChartConfig} secondaryKey="sourceMedium" />} cursor={{ fill: "var(--color-chart-cursor)" }} />
          <Bar dataKey="sessions" fill="var(--color-sessions)" name="Sessions" radius={[0, 5, 5, 0]} />
        </BarChart>
      </ChartContainer>

      <div className="marketing-channel-pie">
        <ChartContainer config={marketingChartConfig} height={280} minHeight={240}>
          <PieChart margin={{ bottom: 8, left: 8, right: 8, top: 8 }}>
            <RechartsTooltip content={<ChartTooltipContent config={marketingChartConfig} secondaryKey="sourceMedium" />} />
            <Pie
              cx="50%"
              cy="48%"
              data={pieData}
              dataKey="sessions"
              innerRadius="48%"
              nameKey="label"
              outerRadius="82%"
              paddingAngle={1.5}
              stroke="var(--color-panel)"
              strokeWidth={2}
            >
              {pieData.map((row, index) => (
                <Cell fill={channelPieColors[index % channelPieColors.length]} key={row.label} />
              ))}
            </Pie>
          </PieChart>
        </ChartContainer>
        <div className="marketing-channel-legend">
          {pieData.map((row, index) => (
            <span key={row.label}>
              <i style={{ background: channelPieColors[index % channelPieColors.length] }} />
              <em>{row.label}</em>
              <strong><MetricCounter value={row.sessions} /></strong>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function SocialAudienceBreakdownChart({ dimension, rows }: { dimension: "age" | "gender" | "region"; rows: MarketingAudienceRow[] }) {
  const data = buildAudienceBreakdown(rows, dimension).slice(0, dimension === "region" ? 10 : 8);

  if (data.length === 0) {
    return <StateBlock type="empty" title="Audience data is preparing" message="This demographic breakdown will appear after Meta returns more follower segments." />;
  }

  const chart = (
    <ChartContainer config={marketingChartConfig} height={dimension === "region" ? Math.max(260, data.length * 39) : 260} minHeight={230}>
      <BarChart data={data} layout="vertical" margin={{ bottom: 8, left: 12, right: 20, top: 8 }}>
        <CartesianGrid horizontal={false} stroke="var(--color-chart-grid)" />
        <XAxis stroke="var(--color-text-subtle)" tickFormatter={formatChartNumber} tickLine={false} type="number" />
        <YAxis dataKey="label" stroke="var(--color-text-subtle)" tickFormatter={formatAxisLabel} tickLine={false} type="category" width={142} />
        <RechartsTooltip content={<ChartTooltipContent config={marketingChartConfig} />} cursor={{ fill: "var(--color-chart-cursor)" }} />
        <Legend content={<ChartLegendContent config={marketingChartConfig} />} />
        <Bar dataKey="facebook" fill="var(--color-facebook)" name="Facebook" radius={[0, 0, 0, 0]} stackId="platform" />
        <Bar dataKey="instagram" fill="var(--color-instagram)" name="Instagram" radius={[0, 5, 5, 0]} stackId="platform" />
      </BarChart>
    </ChartContainer>
  );

  if (dimension === "region") {
    return <div className="marketing-audience-scroll">{chart}</div>;
  }

  return chart;
}

function TopPagesTable({ rows }: { rows: MarketingPageRow[] }) {
  const sortedRows = [...rows].sort((left, right) => right.engagementSecondsPerSession - left.engagementSecondsPerSession);

  return (
    <div className="table-scroll marketing-panel-table-scroll">
      <table className="data-table compact marketing-table marketing-table--tight marketing-table--top-pages">
        <colgroup>
          <col className="marketing-table__name-col" />
          <col className="marketing-table__metric-col" />
          <col className="marketing-table__metric-col" />
          <col className="marketing-table__metric-col" />
          <col className="marketing-table__metric-col" />
        </colgroup>
        <thead>
          <tr>
            <th>Page</th>
            <th>Sessions</th>
            <th>Views</th>
            <th>Events</th>
            <th>Engagement</th>
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => (
            <tr key={row.pagePath}>
              <td>
                <strong>{row.pageTitle}</strong>
                <small>{row.pagePath}</small>
              </td>
              <td><MetricCounter value={row.sessions} /></td>
              <td><MetricCounter value={row.pageViews} /></td>
              <td><MetricCounter value={row.keyEvents} /></td>
              <td><MetricCounter formatValue={(value) => `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}s/session`} value={row.engagementSecondsPerSession} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SocialAccountsTable({ rows }: { rows: MarketingSocialAccountRow[] }) {
  const accountGroups = [...rows]
    .reduce<Map<string, MarketingSocialAccountRow[]>>((groups, row) => {
      const groupKey = stationGroupKey(row.sourceName);
      groups.set(groupKey, [...(groups.get(groupKey) || []), row]);
      return groups;
    }, new Map());
  const sortedRows = [...accountGroups.values()]
    .sort((leftGroup, rightGroup) => {
      const leftTotals = leftGroup.reduce((totals, row) => ({
        followers: totals.followers + row.followers,
        reach: totals.reach + row.reach,
        views: totals.views + row.views + row.videoViews,
        engagements: totals.engagements + row.postEngagements
      }), { engagements: 0, followers: 0, reach: 0, views: 0 });
      const rightTotals = rightGroup.reduce((totals, row) => ({
        followers: totals.followers + row.followers,
        reach: totals.reach + row.reach,
        views: totals.views + row.views + row.videoViews,
        engagements: totals.engagements + row.postEngagements
      }), { engagements: 0, followers: 0, reach: 0, views: 0 });

      return (
        rightTotals.engagements - leftTotals.engagements ||
        rightTotals.views - leftTotals.views ||
        rightTotals.reach - leftTotals.reach ||
        rightTotals.followers - leftTotals.followers
      );
    })
    .flatMap((group) => [...group].sort((left, right) => (
      left.sourceName.localeCompare(right.sourceName) ||
      normalizePlatform(left.platform).localeCompare(normalizePlatform(right.platform))
    )));

  return (
    <div className="table-scroll marketing-panel-table-scroll">
      <table className="data-table compact marketing-table marketing-table--tight marketing-table--social-accounts">
        <colgroup>
          <col className="marketing-table__name-col" />
          <col className="marketing-table__metric-col" />
          <col className="marketing-table__metric-col" />
          <col className="marketing-table__metric-col" />
          <col className="marketing-table__metric-col" />
        </colgroup>
        <thead>
          <tr>
            <th>Account</th>
            <th>Followers</th>
            <th><MarketingMetricHelp label="Reach / Unique Viewers" scope="account" /></th>
            <th><MarketingMetricHelp label="Views" scope="account" /></th>
            <th><MarketingMetricHelp label="Engagements" scope="account" /></th>
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => (
            <tr key={`${row.platform}-${row.sourceName}`}>
              <td>
                <strong>{row.sourceName}</strong>
                <small>
                  <SocialPlatformBadge platform={normalizePlatform(row.platform)} />
                </small>
              </td>
              <td><MetricCounter value={row.followers} /></td>
              <td><MetricCounter value={row.reach} /></td>
              <td><MetricCounter value={row.views + row.videoViews} /></td>
              <td><MetricCounter value={row.postEngagements} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ContentThumbnail({ row }: { row: MarketingContentRow }) {
  const [cachedImage, setCachedImage] = useState<string>();
  const [failedUrls, setFailedUrls] = useState<string[]>([]);
  useEffect(() => {
    let active = true;
    setCachedImage(undefined);
    setFailedUrls([]);
    if (row.thumbnailKey) void getMarketingThumbnail(row.thumbnailKey).then(url => {
      if (active) setCachedImage(url);
    }).catch(() => undefined);
    return () => { active = false; };
  }, [row.thumbnailKey, row.thumbnailUrl, row.mediaUrl]);
  const imageUrl = [cachedImage, row.thumbnailUrl, row.mediaUrl].find(url => url && !failedUrls.includes(url));

  if (!imageUrl) {
    return (
      <div className="marketing-content-thumb marketing-content-thumb--empty" aria-hidden="true">
        <FileText size={18} />
      </div>
    );
  }

  return (
    <img
      alt=""
      className="marketing-content-thumb"
      decoding="async"
      loading="lazy"
      onError={() => setFailedUrls(urls => [...urls, imageUrl!])}
      referrerPolicy="no-referrer"
      src={imageUrl}
    />
  );
}

function OverviewContent({ rows }: { rows: MarketingContentRow[] }) {
  const [period, setPeriod] = useState<ContentPeriod>("threeMonths");
  const range = contentPeriodRange(period);
  const filteredRows = rows.filter(row => contentInPeriod(row.publishedAt, range));
  return <>
    <div className="marketing-content-filter">
      <label>Published <select aria-label="Top Social Content period" value={period} onChange={event => setPeriod(event.target.value as ContentPeriod)}>
        <option value="threeMonths">Last 3 months</option>
        <option value="previousMonth">Last month</option>
        <option value="currentMonth">Current month</option>
      </select></label>
      <span aria-live="polite" title="Publication dates in America/Toronto. Metrics are the latest collected lifetime totals for these posts.">{range.start} to {range.end} · <MetricCounter value={filteredRows.length} /> posts</span>
    </div>
    <ContentTable key={period} rows={filteredRows} />
  </>;
}

function ContentTable({ limit = 12, rows, sortMode = "performance", paginated = false }: { limit?: number; rows: MarketingContentRow[]; sortMode?: "performance" | "published"; paginated?: boolean }) {
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const sortedRows = [...rows].sort((left, right) => {
    if (sortMode === "published") {
      return (
        contentPublishedTime(right) - contentPublishedTime(left) ||
        contentEngagementTotal(right) - contentEngagementTotal(left)
      );
    }

    return (
      compareContentAudience(left, right) ||
      contentPublishedTime(right) - contentPublishedTime(left)
    );
  });
  const visibleRows = paginated
    ? sortedRows.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    : sortedRows.slice(0, limit);

  return (
    <div className="marketing-content-table-wrap">
      <p className="marketing-rate-note">
        {sortMode === "performance"
          ? "Rate = Unique Viewers (Facebook) or Reach (Instagram) / Views x 100. This is an audience-to-views ratio, not engagement rate. Shown as n/a when audience data is missing or Views is missing or zero."
          : "Rate = Engagements / Unique Viewers (Facebook) or Reach (Instagram) x 100. It is shown as n/a when the denominator is missing or zero."}
      </p>
      <div className="table-scroll">
        <table className="data-table marketing-content-table">
          <colgroup>
            <col style={{ width: paginated ? "40%" : "42%" }} />
            <col style={{ width: paginated ? "16%" : "18%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: paginated ? "17%" : "14%" }} />
            <col style={{ width: paginated ? "9%" : "8%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Content</th>
              <th>Published</th>
              <th><MarketingMetricHelp label="Reach / Unique Viewers" scope="content" /></th>
              <th><MarketingMetricHelp label="Views" scope="content" /></th>
              <th><MarketingMetricHelp label="Engagements" scope="content" /></th>
              <th><MarketingMetricHelp label="Rate" scope="content" rateBasis={sortMode === "performance" ? "audienceViews" : "engagement"} /></th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && <tr><td colSpan={6}>No content available.</td></tr>}
            {visibleRows.map((row, index) => (
              <tr key={row.permalinkUrl || `${row.platform}-${row.sourceName}-${row.publishedAt || index}`}>
                <td>
                  <div className="marketing-content-cell">
                    <ContentThumbnail key={row.permalinkUrl || `${row.platform}-${row.sourceName}-${row.publishedAt}`} row={row} />
                    <span className="marketing-content-copy">
                      <strong>{row.caption}</strong>
                      <small className="marketing-content-meta">
                        <SocialPlatformBadge platform={normalizePlatform(row.platform)} />
                        <span>/ {row.sourceName} / {row.contentType}</span>
                      </small>
                      {row.permalinkUrl && (
                        <a href={row.permalinkUrl} rel="noreferrer" target="_blank">
                          Open post
                        </a>
                      )}
                    </span>
                  </div>
                </td>
                <td>{formatDateTime(row.publishedAt)}</td>
                <td><NullableMetricCounter value={row.reach} /></td>
                <td><NullableMetricCounter value={row.views} /></td>
                <td><MetricCounter value={row.engagementTotal || row.likes + row.comments + row.shares + row.saves} /></td>
                <td><NullableMetricCounter formatValue={(value) => percentFormatter.format(value)} value={sortMode === "performance" ? contentAudienceViewRate(row) : row.engagementRate} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {paginated && rows.length > 0 && (
        <nav className="marketing-content-pagination" aria-label="Station content pages">
          <span aria-live="polite">{(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, rows.length)} of <MetricCounter value={rows.length} /> posts</span>
          <div>
            <button type="button" title="Previous page" aria-label="Previous content page" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}><ChevronLeft size={16} aria-hidden="true" /></button>
            <label>Page <select aria-label="Content page" value={currentPage} onChange={(event) => setPage(Number(event.target.value))}>{Array.from({ length: pageCount }, (_, index) => <option key={index + 1} value={index + 1}>{index + 1}</option>)}</select> of {pageCount}</label>
            <button type="button" title="Next page" aria-label="Next content page" disabled={currentPage === pageCount} onClick={() => setPage(currentPage + 1)}><ChevronRight size={16} aria-hidden="true" /></button>
          </div>
        </nav>
      )}
    </div>
  );
}

function StationSocialPage({ stationSummaries }: { stationSummaries: StationSocialSummary[] }) {
  const [selectedStationKey, setSelectedStationKey] = useState<string | undefined>(stationSummaries[0]?.key);

  useEffect(() => {
    if (stationSummaries.length === 0) {
      setSelectedStationKey(undefined);
      return;
    }

    if (!selectedStationKey || !stationSummaries.some((station) => station.key === selectedStationKey)) {
      setSelectedStationKey(stationSummaries[0].key);
    }
  }, [selectedStationKey, stationSummaries]);

  const selectedStation = stationSummaries.find((station) => station.key === selectedStationKey) || stationSummaries[0];

  if (!selectedStation) {
    return (
      <StateBlock
        type="empty"
        title="Station SNS data is unavailable"
        message="Social account and content snapshots are not loaded for station-level reporting yet."
      />
    );
  }

  const visiblePlatforms = selectedStation.platforms.some((platform) => platform.hasData)
    ? selectedStation.platforms.filter((platform) => platform.hasData)
    : selectedStation.platforms;

  return (
    <div className="marketing-performance-stack marketing-station-sns-page">
      <section className="marketing-performance-group">
        <div className="marketing-performance-subhead">
          <div>
            <p>SNS</p>
            <h3>Station Social Performance</h3>
          </div>
          <small>Facebook and Instagram account snapshots grouped by station source name.</small>
        </div>

        <div className="marketing-station-social-layout">
          <aside className="marketing-station-rail" aria-label="Station SNS navigation">
            {stationSummaries.map((station) => (
              <button
                className={station.key === selectedStation.key ? "marketing-station-option active" : "marketing-station-option"}
                key={station.key}
                onClick={() => setSelectedStationKey(station.key)}
                type="button"
              >
                <strong>{station.name}</strong>
                <small>
                  <span><MetricCounter value={station.totals.followers} /> followers</span>
                  <span><MetricCounter value={station.totals.engagements} /> engagements</span>
                </small>
              </button>
            ))}
          </aside>

          <div className="marketing-station-detail">
            <div className="marketing-performance-subgroup-heading marketing-station-detail-heading">
              <span>{selectedStation.name}</span>
              <small><MetricCounter value={selectedStation.totals.contentCount} /> posts / {formatNullablePercent(selectedStation.totals.engagementRate)} engagement rate</small>
            </div>

            <div className="marketing-kpi-grid marketing-kpi-grid--station-social">
              <MarketingKpiTile icon={<Share2 size={17} aria-hidden="true" />} metric={stationMetric("Followers", selectedStation.totals.followers, stationMetricSplit(selectedStation, "followers"))} tone="social" />
              <MarketingKpiTile icon={<Eye size={17} aria-hidden="true" />} metric={stationMetric("Views", selectedStation.totals.views, stationMetricSplit(selectedStation, "views"))} tone="social" />
              <MarketingKpiTile icon={<Megaphone size={17} aria-hidden="true" />} metric={stationMetric("Engagements", selectedStation.totals.engagements, stationMetricSplit(selectedStation, "engagements"))} tone="engagement" />
              <MarketingKpiTile icon={<BarChart3 size={17} aria-hidden="true" />} metric={stationMetric("Reach / Unique Viewers", selectedStation.totals.reach, stationMetricSplit(selectedStation, "reach"))} tone="engagement" />
            </div>

            <div className="marketing-station-platform-grid">
              {visiblePlatforms.map((platform) => (
                <section className="marketing-performance-subgroup" key={platform.platform}>
                  <div className="marketing-performance-subgroup-heading">
                    <SocialPlatformBadge platform={platform.platform} />
                    <small><MetricCounter value={platform.contentCount} /> posts / {formatNullablePercent(platform.engagementRate)} rate</small>
                  </div>
                  <div className="marketing-platform-metric-grid">
                    <StationPlatformMetric helper="Audience size" label="Followers" metricKey="followers" platform={platform} value={platform.followers} />
                    <StationPlatformMetric helper="Views + video views" label="Views" metricKey="views" platform={platform} value={platform.views} />
                    <StationPlatformMetric helper="Account + content signals" label="Engagements" metricKey="engagements" platform={platform} value={platform.engagements} />
                    <StationPlatformMetric helper={platform.platform === "facebook" ? "Page viewers / latest post totals" : "Latest account reach"} label={platform.platform === "facebook" ? "Unique Viewers" : "Reach"} metricKey="reach" platform={platform} value={platform.reach} />
                  </div>
                </section>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="marketing-dashboard-grid marketing-station-content-grid">
        <ChartPanel eyebrow="Station content" icon={<BarChart3 size={18} aria-hidden="true" />} title="Station Content">
          <ContentTable key={selectedStation.key} paginated rows={selectedStation.contentRows} sortMode="published" />
        </ChartPanel>
      </div>
    </div>
  );
}

export function MarketingPage({ view = "overview" }: { view?: MarketingPageView }) {
  const [snapshot, setSnapshot] = useState<MarketingAnalyticsSnapshot | undefined>(() => readCachedMarketingAnalytics());
  const [isLoading, setIsLoading] = useState(!snapshot);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let isMounted = true;
    setIsLoading(!snapshot);
    setError(undefined);

    getMarketingAnalytics({ forceRefresh: Boolean(snapshot) })
      .then((nextSnapshot) => {
        if (isMounted) {
          setSnapshot(nextSnapshot);
        }
      })
      .catch((err: Error) => {
        if (isMounted) {
          setError(err.message);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const trendSummary = useMemo(() => snapshot ? mergeTrendSummary(snapshot) : undefined, [snapshot]);
  const socialPlatforms = useMemo(() => snapshot ? resolveSocialPlatforms(snapshot) : [], [snapshot]);
  const stationSummaries = useMemo(() => snapshot ? buildStationSocialSummaries(snapshot) : [], [snapshot]);

  const insightCards = useMemo(() => {
    if (!snapshot) {
      return [];
    }

    const peakHour = [...snapshot.hourlyTraffic].sort((left, right) => right.clicks - left.clicks)[0];
    const topChannel = snapshot.channels[0];
    const topPage = snapshot.pages[0];
    const topAudience = snapshot.socialAudience.find((row) => row.segment !== "Unknown" && (row.followers || 0) > 0);

    return [
      {
        icon: <Clock3 size={18} aria-hidden="true" />,
        label: "Peak click window",
        value: peakHour ? `${peakHour.hourLabel} ${peakHour.daypart}` : "n/a",
        helper: peakHour ? `${formatNumber(peakHour.clicks)} clicks, ${formatNumber(peakHour.sessions)} sessions` : "No hourly traffic data",
        trend: trendSummary?.clicks
      },
      {
        icon: <Search size={18} aria-hidden="true" />,
        label: "Leading acquisition",
        value: topChannel ? topChannel.channelGroup : "n/a",
        helper: topChannel ? `${topChannel.sourceMedium}, ${formatNumber(topChannel.sessions)} sessions` : "No channel data",
        trend: trendSummary?.sessions
      },
      {
        icon: <FileText size={18} aria-hidden="true" />,
        label: "Most viewed page",
        value: topPage ? topPage.pageTitle : "n/a",
        helper: topPage ? `${formatNumber(topPage.pageViews)} views, ${formatNumber(topPage.sessions)} sessions` : "No page data",
        trend: trendSummary?.pageViews
      },
      {
        icon: <MapPin size={18} aria-hidden="true" />,
        label: "Audience geography",
        value: topAudience?.segment || "n/a",
        helper: topAudience?.followers ? `${formatNumber(topAudience.followers)} social followers` : "Social geography unavailable",
        trend: trendSummary?.socialFollowers
      }
    ];
  }, [snapshot, trendSummary]);

  if (isLoading && !snapshot) {
    return <StateBlock type="loading" title="Loading marketing analytics" message="Collecting social account and content performance snapshots." />;
  }

  if (error || !snapshot) {
    return <StateBlock type="error" title="Marketing analytics unavailable" message={error || "Marketing analytics data is missing."} />;
  }

  const ga4InsightCards = insightCards.filter((card) => card.label !== "Audience geography");
  const socialInsightCards = insightCards.filter((card) => card.label === "Audience geography");
  const facebookPlatform = socialPlatforms.find((row) => normalizePlatform(row.platform) === "facebook");
  const instagramPlatform = socialPlatforms.find((row) => normalizePlatform(row.platform) === "instagram");
  const platformSections = [
    { key: "facebook" as const, label: "Facebook", row: facebookPlatform, trends: snapshot.socialPlatformTrendSummary?.facebook },
    { key: "instagram" as const, label: "Instagram", row: instagramPlatform, trends: snapshot.socialPlatformTrendSummary?.instagram }
  ];

  return (
    <section className="page-stack marketing-page">
      <div className="page-heading marketing-page-heading">
        <div>
          <p>Marketing analytics</p>
          <h2>{view === "station-sns" ? "Station SNS Performance" : "Marketing Performance"}</h2>
        </div>
        <div className="page-heading-actions">
          <span className="timestamp-pill">
            <Clock3 size={15} aria-hidden="true" />
            {snapshot.periodLabel}
          </span>
        </div>
      </div>

      {snapshot.delivery && <p role="status">
        {snapshot.delivery.status === "stale" ? "Update delayed. Showing last saved data. " : ""}
        Data as of {snapshot.asOfDate}. Snapshot saved: {formatDateTime(snapshot.delivery.publishedAt)}.
      </p>}

      {view === "station-sns" ? (
        <StationSocialPage stationSummaries={stationSummaries} />
      ) : (
        <>
          <div className="marketing-performance-stack">
            <section className="marketing-performance-group">
              <div className="marketing-performance-subhead">
                <div>
                  <p>SNS</p>
                  <h3>Social audience and engagement</h3>
                </div>
                <small>Combined social performance with Facebook and Instagram split out.</small>
              </div>
              <div className="marketing-sns-section-grid">
                <section className="marketing-performance-subgroup marketing-performance-subgroup--overall">
                  <div className="marketing-performance-subgroup-heading">
                    <span>Overall</span>
                    <small className="marketing-social-platform-pair">
                      <SocialPlatformBadge platform="facebook" />
                      <span>+</span>
                      <SocialPlatformBadge platform="instagram" />
                    </small>
                  </div>
                  <div className="marketing-kpi-grid marketing-kpi-grid--sns-overall">
                    <MarketingKpiTile icon={<Share2 size={17} aria-hidden="true" />} metric={withHelper(snapshot.social.followers, socialPlatformSplit(socialPlatforms, "followers"))} tone="social" trend={trendSummary?.socialFollowers} trendValueMode="delta" />
                    <MarketingKpiTile icon={<Eye size={17} aria-hidden="true" />} metric={withHelper(snapshot.social.views, socialPlatformSplit(socialPlatforms, "views"))} tone="social" trend={trendSummary?.socialViews} />
                    <MarketingKpiTile icon={<Megaphone size={17} aria-hidden="true" />} metric={withHelper(snapshot.social.engagements, socialPlatformSplit(socialPlatforms, "engagements"))} tone="engagement" trend={trendSummary?.socialEngagements} />
                    {socialInsightCards.map((card) => (
                      <MarketingInsightCard card={card} key={card.label} />
                    ))}
                  </div>
                </section>

                {platformSections.map((section) => (
                  <section className="marketing-performance-subgroup" key={section.key}>
                    <div className="marketing-performance-subgroup-heading">
                      <SocialPlatformBadge platform={section.key} />
                      <small>{section.row ? <><MetricCounter value={section.row.followers} /> followers</> : "No account snapshot"}</small>
                    </div>
                    <div className="marketing-platform-metric-grid">
                      <MarketingPlatformMetric helper="Audience size" label="Followers" tone={section.key} trend={section.trends?.followers} trendValueMode="delta" value={socialPlatformValue(section.row, "followers")} />
                      <MarketingPlatformMetric helper="Views + video views" label="Views" tone={section.key} trend={section.trends?.views} value={socialPlatformValue(section.row, "views")} />
                      <MarketingPlatformMetric helper="Account + content signals" label="Engagements" tone={section.key} trend={section.trends?.engagements} value={socialPlatformValue(section.row, "engagements")} />
                      <MarketingPlatformMetric helper={section.key === "facebook" ? "Page viewers / latest post totals" : "Latest account reach"} label={section.key === "facebook" ? "Unique Viewers" : "Reach"} tone={section.key} trend={section.trends?.reach} value={section.row?.reach || 0} />
                    </div>
                  </section>
                ))}
              </div>

              <div className="marketing-section-heading">
                <div>
                  <p>Audience</p>
                  <h3>Social Demographics</h3>
                </div>
                <small>Meta follower breakdowns separated by age, gender, and region.</small>
              </div>

              <div className="marketing-dashboard-grid marketing-dashboard-grid--audience">
                <ChartPanel eyebrow="Demographics" icon={<Users size={18} aria-hidden="true" />} title="Age Breakdown" trend={trendSummary?.socialFollowers}>
                  <SocialAudienceBreakdownChart dimension="age" rows={snapshot.socialAudience} />
                </ChartPanel>
                <ChartPanel eyebrow="Demographics" icon={<Users size={18} aria-hidden="true" />} title="Gender Breakdown" trend={trendSummary?.socialFollowers}>
                  <SocialAudienceBreakdownChart dimension="gender" rows={snapshot.socialAudience} />
                </ChartPanel>
                <ChartPanel eyebrow="Demographics" icon={<MapPin size={18} aria-hidden="true" />} title="Region / City Breakdown" trend={trendSummary?.socialFollowers}>
                  <SocialAudienceBreakdownChart dimension="region" rows={snapshot.socialAudience} />
                </ChartPanel>
              </div>

              <ChartPanel eyebrow="Creative performance" icon={<BarChart3 size={18} aria-hidden="true" />} title="Top Social Content" trend={trendSummary?.socialEngagements}>
                <OverviewContent rows={snapshot.content} />
              </ChartPanel>
            </section>

            {showWebsiteAnalytics && <section className="marketing-performance-group">
              <div className="marketing-performance-subhead">
                <div>
                  <p>Google Analytics</p>
                  <h3>Website performance</h3>
                </div>
                <small>GA4 active users, event count, key events, and new users.</small>
              </div>
              <div className="marketing-kpi-grid marketing-kpi-grid--ga4">
                <MarketingKpiTile icon={<Users size={17} aria-hidden="true" />} metric={snapshot.web.activeUsers} tone="traffic" trend={trendSummary?.activeUsers} />
                <MarketingKpiTile icon={<BarChart3 size={17} aria-hidden="true" />} metric={snapshot.web.eventCount} tone="traffic" trend={trendSummary?.eventCount} />
                <MarketingKpiTile icon={<TrendingUp size={17} aria-hidden="true" />} metric={snapshot.web.keyEvents} tone="engagement" trend={trendSummary?.keyEvents} />
                <MarketingKpiTile icon={<Users size={17} aria-hidden="true" />} metric={snapshot.web.newUsers} tone="engagement" trend={trendSummary?.newUsers} />
              </div>
              <div className="marketing-insight-grid marketing-insight-grid--ga4">
                {ga4InsightCards.map((card) => (
                  <MarketingInsightCard card={card} key={card.label} />
                ))}
              </div>
              <div className="marketing-dashboard-grid marketing-dashboard-grid--wide">
                <ChartPanel eyebrow="Website behavior" icon={<TrendingUp size={18} aria-hidden="true" />} title="Daily Traffic and Clicks" trend={trendSummary?.sessions}>
                  <DailyTrendChart snapshot={snapshot} />
                </ChartPanel>
                <ChartPanel eyebrow="Click timing" icon={<Clock3 size={18} aria-hidden="true" />} title="Hourly Click Pattern" trend={trendSummary?.clicks}>
                  <HourlyTrafficChart snapshot={snapshot} />
                </ChartPanel>
              </div>
              <div className="marketing-dashboard-grid marketing-dashboard-grid--single">
                <ChartPanel eyebrow="Acquisition" icon={<Search size={18} aria-hidden="true" />} title="Top Channels" trend={trendSummary?.sessions}>
                  <ChannelChart rows={snapshot.channels} />
                </ChartPanel>
              </div>
            </section>}
          </div>

          <div className={`marketing-dashboard-grid${showWebsiteAnalytics ? "" : " marketing-dashboard-grid--single"}`}>
            {showWebsiteAnalytics && <ChartPanel eyebrow="Website content" icon={<FileText size={18} aria-hidden="true" />} title="Top Pages" trend={trendSummary?.pageViews}>
              <TopPagesTable rows={snapshot.pages} />
            </ChartPanel>}
            <ChartPanel eyebrow="Social accounts" icon={<Share2 size={18} aria-hidden="true" />} title="Account Performance" trend={trendSummary?.socialEngagements}>
              <SocialAccountsTable rows={snapshot.socialAccounts} />
            </ChartPanel>
          </div>
        </>
      )}
    </section>
  );
}
